import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { api, redirectToLogin } from './api-client';
import { logger } from './telemetry';
import type { Organization } from '@/types';

interface AuthIdentity {
    sub: string;
    email?: string;
    name?: string;
    organization_id?: string;
    account_id?: string;
    application_id?: string;
    is_machine: boolean;
    is_admin: boolean;
    roles?: string[];
}

interface AuthState {
    identity: AuthIdentity | null;
    currentOrganization: Organization | null;
    organizations: Organization[];
    permissions: string[];
    isLoading: boolean;
}

interface AuthContextValue extends AuthState {
    login: () => void;
    logout: () => void;
    setCurrentOrganization: (organizationId: string) => void;
    fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>;
}

function getErrorDetails(error: unknown): { message: string; status?: number } {
    const candidate = error as { message?: string; response?: { status?: number } };
    return {
        message: candidate?.message ?? 'unknown error',
        status: candidate?.response?.status,
    };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        identity: null,
        currentOrganization: null,
        organizations: [],
        permissions: [],
        isLoading: true,
    });

    useEffect(() => {
        logger.debug('admin-ui:auth-provider:bootstrap - Starting session bootstrap');
        api.get('/auth/session', {
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
            _skipAuthRedirect: true,
        })
            .then(({ data }) => {
                if (!data.authenticated) {
                    logger.info('admin-ui:auth-provider:bootstrap - Session unauthenticated');
                    setState((s) => ({ ...s, isLoading: false }));
                    return;
                }
                logger.debug('admin-ui:auth-provider:bootstrap - Session authenticated, fetching profile');
                return api.get('/auth/me', { _skipAuthRedirect: true });
            })
            .then((resp) => {
                if (!resp) return;
                if (resp.data?.needs_registration) {
                    logger.info('admin-ui:auth-provider:bootstrap - User needs registration, redirecting to register', {
                        subject: resp.data.subject,
                    });
                    window.location.href = '/api/auth/login?prompt=register';
                    return;
                }
                logger.info('admin-ui:auth-provider:bootstrap - Profile loaded', {
                    subject: resp.data?.identity?.sub,
                    organizations: Array.isArray(resp.data?.organizations) ? resp.data.organizations.length : 0,
                });
                setState({
                    identity: resp.data.identity,
                    currentOrganization: resp.data.currentOrganization,
                    organizations: Array.isArray(resp.data?.organizations) ? resp.data.organizations : [],
                    permissions: Array.isArray(resp.data?.permissions) ? resp.data.permissions : [],
                    isLoading: false,
                });
            })
            .catch((error: unknown) => {
                const details = getErrorDetails(error);
                logger.error('admin-ui:auth-provider:bootstrap - Failed to initialize auth state', {
                    message: details.message,
                    status: details.status,
                });
                setState((s) => ({ ...s, isLoading: false }));
                if (details.status === 401) {
                    redirectToLogin();
                }
            });
    }, []);

    const login = () => {
        logger.info('admin-ui:auth-provider:login - User initiated login');
        redirectToLogin();
    };

    const logout = () => {
        logger.info('admin-ui:auth-provider:logout - User initiated logout');
        window.location.href = '/api/auth/logout';
    };

    const fetchWithAuth = async (url: string, init?: RequestInit): Promise<Response> => {
        return fetch(url, { credentials: 'include', ...init });
    };

    const setCurrentOrganization = async (organizationId: string) => {
        try {
            await api.put('/user/current-organization', { organization_id: organizationId });
            const organization = state.organizations.find((t) => t.id === organizationId);
            if (organization) {
                logger.info('admin-ui:auth-provider:setCurrentOrganization - Organization switched', {
                    organizationId,
                });
                setState((s) => ({ ...s, currentOrganization: organization }));
            }
        } catch (error: unknown) {
            const details = getErrorDetails(error);
            logger.warn('admin-ui:auth-provider:setCurrentOrganization - Failed API update, using local fallback', {
                organizationId,
                message: details.message,
                status: details.status,
            });
            const organization = state.organizations.find((t) => t.id === organizationId);
            if (organization) setState((s) => ({ ...s, currentOrganization: organization }));
        }
    };

    return (
        <AuthContext.Provider value={{ ...state, login, logout, setCurrentOrganization, fetchWithAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

/** @deprecated Use PermissionGate instead */
export function AdminRoute({ children }: { children: ReactNode }) {
    const { identity } = useAuth();
    if (!identity?.roles?.includes('platform_admin') && !identity?.is_admin) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

export function ProtectedRoute() {
    const { identity, organizations, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!identity) {
        logger.warn('admin-ui:protected-route:render - Missing identity, redirecting to login', {
            path: location.pathname,
        });
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // User must belong to at least one organization; otherwise redirect to login
    if (!organizations || organizations.length === 0) {
        logger.warn('admin-ui:protected-route:render - User has no organizations, redirecting to login', {
            subject: identity.sub,
            path: location.pathname,
        });
        return (
            <Navigate
                to="/login"
                state={{ from: location, error: 'no_organizations' }}
                replace
            />
        );
    }

    return <Outlet />;
}
